<template>
  <card title="State placement">
    <div class="bg-gray-50 dark:bg-gray-800/50 aspect-[4/3] border-b border-gray-300 dark:border-gray-800">
      <div id="map" aria-hidden="true" class="h-full flex items-center"/>
    </div>

    <div class="flex flex-col justify-center px-1 py-2">
      <a
        v-for="state in topStates"
        :key="state.code"
        :href="route('states.show', state)"
        class="py-1 px-3 rounded-md flex justify-between gap-3 text-sm text-inherit hover:text-inherit hover:bg-gray-400/10"
        :aria-label="`${state.count} grads from the Class of ${state.year} were employed in ${state.name}.`"
        data-balloon-pos="up-left"
      >
        <div class="flex items-center gap-2">
          <div class="size-2 rounded-full" :style="{ backgroundColor: state.bg }"/>
          <div>
            {{ state.name }}
            <span v-if="state.year < config('employment_year')" class="text-gray-500">
              ({{ state.year }} grads)
            </span>
          </div>
        </div>
        <span class="font-semibold text-blue dark:text-white">
          {{ state.percent }}%
        </span>
      </a>
      <div
        v-for="category in categories"
        :key="category.name"
        class="py-1 px-3 rounded-md flex justify-between gap-3 text-sm"
      >
        <div class="flex items-center gap-2">
          <div class="size-2 rounded-full" :style="{ backgroundColor: category.bg }"/>
          <span>
            {{ category.name }}
          </span>
        </div>
        <span class="font-semibold text-blue dark:text-white">
          {{ category.percent }}%
        </span>
      </div>
    </div>
  </card>
</template>

<script setup>
import Card from "@/components/cards/Card.vue"
import colors from "@/colors"
import mapdata from "@/maps/mapdata"
import mapinfo from "@/maps/mapinfo"
import route from "ziggy-js"
import usmap from "@/maps/usmap"
import { computed, watch } from "vue"
import { config } from "@/support"
import { keyBy } from "lodash-es"
import { useStates } from "@/composables/useStates"

const props = defineProps({
  school: { type: Object, required: true },
})

const { states } = useStates()

const categories = computed(() => [
  {
    name: "Employed, unknown state",
    percent: props.school.employed_unknown_state_percent,
    bg: colors.green[400],
  },
  {
    name: "Employed abroad",
    percent: props.school.foreign_employment_percent,
    bg: colors.green[400],
  },
  {
    name: "Non-employed",
    percent: props.school.non_employed_percent,
    bg: colors.gray[400],
  },
])

const items = computed(() => {
  return states.map(state => {
    const topState = topStatesMap.value[state.code]

    const percent = topState?.percent || 0
    const bg = topState?.bg || colors.gray[200]

    return {
      ...state,
      hide: "no",
      color: bg,
      hover_color: bg,
      description: `${percent}% of grads`,
    }
  })
})

const labels = computed(() => {
  return states.map((state) => {
    const color = topStatesMap.value[state.code]?.color || colors.gray[500]

    return {
      ...mapdata.labels[state.code],
      color,
      hover_color: color,
    }
  })
})

const topStates = computed(() => props.school.top_employment_location.map((state) => {
  let bg = colors.gray[300]
  let color = colors.blue[900]

  if (state.percent > 40) {
    bg = colors.blue[800]
    color = colors.gray[100]
  } else if (state.percent > 20) {
    bg = colors.blue[500]
    color = colors.gray[100]
  } else if (state.percent > 10) {
    bg = colors.blue[400]
  } else if (state.percent > 3) {
    bg = colors.blue[300]
  } else if (state.percent > 0) {
    bg = colors.blue[200]
  }

  return { ...state, bg, color }
}))

const topStatesMap = computed(() => {
  return keyBy(topStates.value, "code")
})

watch([items, labels], () => {
  usmap.mapdata = {
    ...mapdata,
    main_settings: {
      ...mapdata.main_settings,
    },
    state_specific: keyBy(items.value, "code"),
    labels: labels.value,
  }

  usmap.mapinfo = mapinfo

  usmap.load()
})
</script>
